import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  MapPin,
  Building2,
  Calendar,
  CheckCircle,
  XCircle,
  ChevronDown,
  Car
} from 'lucide-react';
import { api } from '../../../utils/api';

const FareList = ({ onEdit, onView }) => {
  const [filters, setFilters] = useState({
    search: '',
    isActive: '',
    regionalOffice: '',
    page: 1,
    limit: 10
  });
  
  const [expandedVehicleTypes, setExpandedVehicleTypes] = useState({});

  const { data: regionalOfficesData } = useQuery(
    'regional-offices',
    () => api.get('/regional-offices', { params: { limit: 1000 } })
  );

  const { data, isLoading, error, refetch } = useQuery(
    ['fare-configs', filters],
    () => api.get('/fare', { params: filters }),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        console.log('Fare configs API response:', data);
      },
      onError: (error) => {
        console.error('Fare configs API error:', error);
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/fare/${id}`),
    {
      onSuccess: () => {
        toast.success('Fare configuration deleted successfully');
        refetch();
      },
      onError: () => {
        toast.error('Failed to delete fare configuration');
      }
    }
  );

  const toggleStatusMutation = useMutation(
    ({ id, isActive }) => api.patch(`/fare/${id}/status`, { isActive }),
    {
      onSuccess: () => {
        toast.success('Status updated successfully');
        refetch();
      },
      onError: () => {
        toast.error('Failed to update status');
      }
    }
  );

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this fare configuration?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleStatus = (id, currentStatus) => {
    toggleStatusMutation.mutate({ id, isActive: !currentStatus });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const toggleVehicleTypes = (configId) => {
    setExpandedVehicleTypes(prev => ({
      ...prev,
      [configId]: !prev[configId]
    }));
  };

  if (error) {
    return (
      <div className="empty-state">
        <XCircle className="empty-state-icon" />
        <h3 className="empty-state-title">Error Loading Configurations</h3>
        <p className="empty-state-description">
          Failed to load fare configurations. Please try again.
        </p>
      </div>
    );
  }

  const configs = data?.data?.data?.configs || [];
  const pagination = data?.data?.data?.pagination || {};

  // Debug logging
  console.log('FareList - Full data:', data);
  console.log('FareList - Configs:', configs);
  console.log('FareList - Pagination:', pagination);
  console.log('FareList - Is loading:', isLoading);
  console.log('FareList - Error:', error);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="input pl-10"
                  placeholder="Search by region code..."
                />
              </div>
            </div>

            <div>
              <label className="form-label">Status</label>
              <select
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
                className="input"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label className="form-label">Regional Office</label>
              <select
                value={filters.regionalOffice}
                onChange={(e) => handleFilterChange('regionalOffice', e.target.value)}
                className="input"
              >
                <option value="">All Offices</option>
                {regionalOfficesData?.data?.data?.offices?.map((office) => (
                  <option key={office._id} value={office._id}>
                    {office.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setFilters({ search: '', isActive: '', regionalOffice: '', page: 1, limit: 10 })}
                className="btn btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Configurations List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Fare Configurations</h3>
        </div>
        <div className="card-body p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="loading-spinner"></div>
            </div>
          ) : configs.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon text-4xl">₹</span>
              <h3 className="empty-state-title">No Configurations Found</h3>
              <p className="empty-state-description">
                No fare configurations match your current filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Region
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Regional Office
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle Types
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {configs.map((config) => (
                    <tr key={config._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {config.regionCode}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">
                            {config.regionalOffice?.name || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="relative">
                          <button
                            onClick={() => toggleVehicleTypes(config._id)}
                            className="flex items-center justify-between w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md border border-gray-200 text-sm"
                          >
                            <div className="flex items-center space-x-2">
                              <Car className="h-4 w-4 text-gray-400" />
                              <span className="font-medium text-gray-700">
                                {config.vehicleTypes ? Object.keys(config.vehicleTypes).length : 0} Vehicle Types
                              </span>
                            </div>
                            <ChevronDown 
                              className={`h-4 w-4 text-gray-400 transition-transform ${
                                expandedVehicleTypes[config._id] ? 'rotate-180' : ''
                              }`}
                            />
                          </button>
                          
                          {expandedVehicleTypes[config._id] && config.vehicleTypes && (
                            <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md max-h-48 overflow-y-auto whitespace-normal">
                              {Object.entries(config.vehicleTypes).map(([type, details]) => (
                                <div key={type} className="px-3 py-2 border-b border-gray-200 last:border-b-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {type.replace('_', ' ')}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {details.baseFare > 0 ? 'Base Fare' : 'Range Pricing'}
                                    </span>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <div>Min: ₹{details.minFare}</div>
                                    <div>Max: ₹{details.maxFare}</div>
                                    {details.baseFare > 0 && (
                                      <>
                                        <div>Base: ₹{details.baseFare}</div>
                                        <div>Wait: ₹{details.waitTimeCostPerMin}/min</div>
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          config.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {config.isActive ? (
                            <>
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(config.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => onView(config)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onEdit(config)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(config._id, config.isActive)}
                            className={`${
                              config.isActive 
                                ? 'text-red-600 hover:text-red-900' 
                                : 'text-green-600 hover:text-green-900'
                            }`}
                            title={config.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {config.isActive ? (
                              <XCircle className="h-4 w-4" />
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDelete(config._id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > 1 && (
          <div className="card-footer">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((pagination.current - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.current * pagination.limit, pagination.totalRecords)} of{' '}
                {pagination.totalRecords} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current - 1)}
                  disabled={pagination.current === 1}
                  className="btn btn-outline btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Page {pagination.current} of {pagination.total}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.current + 1)}
                  disabled={pagination.current === pagination.total}
                  className="btn btn-outline btn-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FareList;
